import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, CheckCircle2, Save } from 'lucide-react';
import { money, round2, toAmount, formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { enqueueDraft } from '../../lib/offlineQueue.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { WeightInput } from '../../components/ui/WeightInput.jsx';

const mobilePattern = /^[6-9]\d{9}$/;

function newItemRow(rate = '0') {
  return { key: crypto.randomUUID(), particulars: '', rate, grossWeight: '0', netWeight: '0', remarks: '' };
}

export default function GoldPurchaseForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });
  const ratesQuery = useQuery({ queryKey: ['rates', 'today'], queryFn: () => api.get('/rates/today') });

  const [customer, setCustomer] = useState({ id: null, name: '', mobile: '', address: '' });
  const [purityId, setPurityId] = useState('');
  const [purityLabel, setPurityLabel] = useState('');
  const [ratePerGram, setRatePerGram] = useState('0');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [adjustedInvoice, setAdjustedInvoice] = useState(null);
  const [sellerIsRegistered, setSellerIsRegistered] = useState(false);
  const [sellerGstin, setSellerGstin] = useState('');
  const [idProofType, setIdProofType] = useState('');
  const [idProofNumber, setIdProofNumber] = useState('');
  const [items, setItems] = useState([newItemRow()]);
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null);

  // --- Customer lookup by mobile -------------------------------------------
  const debouncedMobile = useDebouncedValue(customer.mobile, 400);
  const canLookup = mobilePattern.test(debouncedMobile) && !customer.id;
  const lookupQuery = useQuery({
    queryKey: ['customers', 'by-mobile', debouncedMobile],
    queryFn: () => api.get(`/customers/by-mobile/${debouncedMobile}`),
    enabled: canLookup,
  });
  const matches = canLookup ? lookupQuery.data ?? [] : [];

  const selectCustomer = (c) => {
    setCustomer({ id: c.id, name: c.name, mobile: c.mobile ?? '', address: c.address_line1 ?? '' });
  };
  const clearCustomerMatch = () => setCustomer((c) => ({ ...c, id: null }));

  // --- Invoice lookup for "adjust against sale" -----------------------------
  const debouncedInvoiceSearch = useDebouncedValue(invoiceSearch, 400);
  const invoiceLookupQuery = useQuery({
    queryKey: ['invoices', 'search', debouncedInvoiceSearch],
    queryFn: () => api.get('/invoices', { query: { search: debouncedInvoiceSearch, pageSize: 5 } }),
    enabled: paymentMode === 'adjust_against_sale' && debouncedInvoiceSearch.length >= 3 && !adjustedInvoice,
  });
  const invoiceMatches = invoiceLookupQuery.data?.data ?? [];

  // --- Purity / rate ----------------------------------------------------------
  const purityById = useMemo(() => new Map((puritiesQuery.data ?? []).map((p) => [p.id, p])), [puritiesQuery.data]);
  const rateByPurityId = useMemo(
    () => new Map((ratesQuery.data?.purities ?? []).filter((p) => p.ratePerGram).map((p) => [p.purityId, p.ratePerGram])),
    [ratesQuery.data],
  );

  const applyPurity = (id) => {
    const purity = purityById.get(Number(id));
    const rate = rateByPurityId.get(Number(id));
    setPurityId(id);
    setPurityLabel(purity ? `${purity.metal} ${purity.label}` : '');
    if (rate) {
      setRatePerGram(String(rate));
      setItems((rows) => rows.map((r) => (r.rate === '0' || !r.particulars ? { ...r, rate: String(rate) } : r)));
    }
  };

  const updateItem = (key, patch) => setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeItem = (key) => setItems((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  const addItem = () => setItems((rows) => [...rows, newItemRow(ratePerGram)]);

  // --- Live totals -------------------------------------------------------------
  const lines = items.map((item) => ({
    ...item,
    value: round2(money(item.netWeight || 0).times(money(item.rate || 0))),
  }));
  const totalGrossWeight = lines.reduce((acc, l) => acc.plus(l.grossWeight || 0), money(0));
  const totalNetWeight = lines.reduce((acc, l) => acc.plus(l.netWeight || 0), money(0));
  const totalAmount = round2(lines.reduce((acc, l) => acc.plus(l.value), money(0)));

  const rateGateOpen = ratesQuery.data?.gateOpen;

  // --- Submit --------------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/gold-purchases', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['gold-purchases'] });
      navigate(`/gold-purchases/${result.id}`, { state: { justCreated: true } });
    },
    onError: (err, payload) => {
      // Only a draft-save queues offline — issuing needs a server-allocated
      // number at the moment it happens (docs/project-plan.html §13).
      if (err instanceof ApiClientError && err.isOffline && pendingAction === 'draft') {
        enqueueDraft({ endpoint: '/gold-purchases', payload, docType: 'gold_purchase', label: customer.name || 'Untitled gold purchase' })
          .then(() => navigate('/gold-purchases', { state: { savedOffline: true } }))
          .catch(() => setSubmitError('Could not save this draft on this device either. Please try again.'));
        return;
      }
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this bill. Please try again.');
    },
  });

  const buildPayload = (saveAsDraft = false) => ({
    saveAsDraft,
    customerId: customer.id ?? undefined,
    customerName: customer.name,
    customerMobile: customer.mobile || undefined,
    customerAddress: customer.address || undefined,
    purityId: purityId ? Number(purityId) : undefined,
    purityLabel: purityLabel || undefined,
    ratePerGram: Number(ratePerGram),
    paymentMode,
    adjustedInvoiceId: paymentMode === 'adjust_against_sale' ? adjustedInvoice?.id : undefined,
    sellerIsRegistered,
    sellerGstin: sellerIsRegistered ? sellerGstin || undefined : undefined,
    idProofType: idProofType || undefined,
    idProofNumber: idProofNumber || undefined,
    items: items.map((item) => ({
      particulars: item.particulars,
      rate: Number(item.rate),
      grossWeight: Number(item.grossWeight),
      netWeight: Number(item.netWeight),
      remarks: item.remarks || undefined,
    })),
    notes: notes || undefined,
  });

  const baseValid =
    !!customer.name.trim() &&
    items.every((i) => i.particulars.trim() && Number(i.netWeight) > 0 && Number(i.rate) > 0) &&
    (paymentMode !== 'adjust_against_sale' || !!adjustedInvoice);
  const canSubmit = baseValid && rateGateOpen !== false;
  const canSaveDraft = baseValid;

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('issue');
    createMutation.mutate(buildPayload(false));
  };

  const onSaveDraft = () => {
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('draft');
    createMutation.mutate(buildPayload(true));
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <h1 className="font-display text-2xl font-semibold tracking-tight">New gold purchase</h1>

      {ratesQuery.data && !rateGateOpen ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          Today&rsquo;s rate hasn&rsquo;t been fully entered — billing is blocked until it is.
        </p>
      ) : null}

      {/* Seller ------------------------------------------------------------ */}
      <section className="card flex flex-col gap-4 p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Seller</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <TextField
              label="Mobile"
              inputMode="numeric"
              value={customer.mobile}
              onChange={(e) => setCustomer((c) => ({ ...c, mobile: e.target.value, id: c.id && e.target.value !== c.mobile ? null : c.id }))}
              hint={customer.id ? 'Matched to a saved customer' : 'Type 10 digits to search'}
            />
            {matches.length > 0 ? (
              <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                {matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => selectCustomer(m)}
                    className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-surface-2"
                  >
                    <Search size={14} className="text-muted" aria-hidden="true" />
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted">{m.city}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <TextField label="Name" required value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
          <TextField label="Address" value={customer.address} onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))} className="sm:col-span-2" />
        </div>
        {customer.id ? (
          <button type="button" onClick={clearCustomerMatch} className="self-start text-sm text-primary hover:underline">
            Not this person — enter details manually
          </button>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm font-medium text-content">
            <input type="checkbox" checked={sellerIsRegistered} onChange={(e) => setSellerIsRegistered(e.target.checked)} className="h-4 w-4" />
            Seller is a registered dealer
          </label>
          {sellerIsRegistered ? (
            <TextField label="Seller GSTIN" value={sellerGstin} onChange={(e) => setSellerGstin(e.target.value)} error={fieldErrors.sellerGstin} />
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField label="ID proof" value={idProofType} onChange={(e) => setIdProofType(e.target.value)}>
            <option value="">Not recorded</option>
            <option value="aadhaar">Aadhaar</option>
            <option value="pan">PAN</option>
            <option value="voter_id">Voter ID</option>
            <option value="driving_licence">Driving licence</option>
            <option value="passport">Passport</option>
            <option value="other">Other</option>
          </SelectField>
          {idProofType ? (
            <TextField label="ID proof number" value={idProofNumber} onChange={(e) => setIdProofNumber(e.target.value)} />
          ) : null}
        </div>
      </section>

      {/* Purity & payment ---------------------------------------------------- */}
      <section className="card grid grid-cols-1 gap-3 p-5 sm:grid-cols-3">
        <SelectField label="Purity" value={purityId} onChange={(e) => applyPurity(e.target.value)}>
          <option value="">Custom / not listed</option>
          {(puritiesQuery.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.metal} {p.label}
            </option>
          ))}
        </SelectField>
        <TextField label="Reference rate/g" type="number" inputMode="decimal" step="0.01" value={ratePerGram} onChange={(e) => setRatePerGram(e.target.value)} hint="Used to prefill new item rows" />
        <SelectField label="Payment mode" value={paymentMode} onChange={(e) => { setPaymentMode(e.target.value); setAdjustedInvoice(null); }}>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="card">Card</option>
          <option value="netbanking">Net banking</option>
          <option value="cheque">Cheque</option>
          <option value="adjust_against_sale">Adjust against a sale invoice</option>
        </SelectField>

        {paymentMode === 'adjust_against_sale' ? (
          <div className="relative sm:col-span-3">
            {adjustedInvoice ? (
              <div className="flex items-center justify-between rounded-card border border-border bg-surface-2 px-3.5 py-2.5 text-sm">
                <span>
                  Adjusting against <b>{adjustedInvoice.invoice_no}</b> — {formatINR(adjustedInvoice.amount_payable)}
                </span>
                <button type="button" onClick={() => setAdjustedInvoice(null)} className="text-primary hover:underline">
                  Change
                </button>
              </div>
            ) : (
              <>
                <TextField
                  label="Find the invoice"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  placeholder="Invoice number, customer or mobile…"
                  error={fieldErrors.adjustedInvoiceId}
                />
                {invoiceMatches.length > 0 ? (
                  <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                    {invoiceMatches.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() => setAdjustedInvoice(inv)}
                        className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm hover:bg-surface-2"
                      >
                        <span className="font-medium">{inv.invoice_no}</span>
                        <span className="text-muted">{inv.customer_name} · {formatINR(inv.amount_payable)}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </section>

      {/* Items ------------------------------------------------------------ */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Items purchased</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addItem}>
            <Plus size={15} aria-hidden="true" /> Add item
          </Button>
        </div>

        {items.map((item, index) => (
          <div key={item.key} className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <TextField label="Particulars" required value={item.particulars} onChange={(e) => updateItem(item.key, { particulars: e.target.value })} />
              </div>
              {items.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeItem(item.key)}
                  className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
                  aria-label="Remove item"
                >
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <WeightInput label="Gross weight" value={item.grossWeight} onChange={(v) => updateItem(item.key, { grossWeight: v })} />
              <WeightInput label="Net weight" required value={item.netWeight} onChange={(v) => updateItem(item.key, { netWeight: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Rate per gram" type="number" inputMode="decimal" step="0.01" required value={item.rate} onChange={(e) => updateItem(item.key, { rate: e.target.value })} />
              <TextField label="Remarks" value={item.remarks} onChange={(e) => updateItem(item.key, { remarks: e.target.value })} />
            </div>

            <div className="flex items-center justify-between border-t border-border pt-2.5 text-sm">
              <span className="text-muted">{formatWeight(item.netWeight)} net</span>
              <span className="tabular font-semibold">{formatINR(lines[index].value)}</span>
            </div>
          </div>
        ))}
      </section>

      <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

      {/* Totals -------------------------------------------------------------- */}
      <section className="card ml-auto w-full max-w-sm p-5">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Total gross weight</dt><dd className="tabular">{formatWeight(totalGrossWeight)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Total net weight</dt><dd className="tabular">{formatWeight(totalNetWeight)}</dd></div>
          <div className="mt-1 flex justify-between border-t border-border pt-2 text-base font-semibold text-primary"><dt>Amount</dt><dd className="tabular">{formatINR(totalAmount)}</dd></div>
        </dl>
      </section>

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-between gap-3 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <div className="tabular text-lg font-semibold">{formatINR(totalAmount)}</div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            disabled={!canSaveDraft}
            loading={createMutation.isPending && pendingAction === 'draft'}
            onClick={onSaveDraft}
          >
            <Save size={18} aria-hidden="true" /> Save as draft
          </Button>
          <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending && pendingAction === 'issue'}>
            <CheckCircle2 size={18} aria-hidden="true" /> Save bill
          </Button>
        </div>
      </div>
    </form>
  );
}
